import React, { useState } from 'react';
import axios from 'axios';
import {
  XCircleIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BanknotesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, getStatusColor, getStatusLabel } from '../services/api';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ITEMS_PER_PAGE = 12;

export const generateHealthContributionsStatementPdfForPolicy = async (policy) => {
  if (!policy) return;

  try {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Helper montants (aligné sur SavingsDetailsModal)
    const formatAmount = (value) => {
      const num = Number(value || 0);
      if (Number.isNaN(num)) return '0';
      const rounded = Math.round(num).toString();
      return rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    // Logo
    const addLogo = () => new Promise((resolve) => {
      const img = new Image();
      img.src = '/assets/logo-fopam-v2.png';
      img.onload = () => {
        try {
          doc.addImage(img, 'PNG', 15, 10, 22, 22);
        } catch (_) {
          // ignorer l'erreur d'image
        }
        resolve();
      };
      img.onerror = () => resolve();
    });

    await addLogo();

    // En-tête texte
    doc.setFontSize(18);
    doc.setTextColor(25, 25, 25);
    doc.text('FOPAM+', 42, 20);

    doc.setFontSize(11);
    doc.setTextColor(90, 90, 90);
    doc.text('Relevé de cotisations', 42, 27);

    // Informations de la police / mutualiste
    let y = 40;
    const mutualiste = policy.subscription?.mutualiste;
    const offerName = policy.subscription?.offer?.name || '';

    const policyInfoLines = [
      `Police : ${policy.policyNumber || ''}`,
      offerName ? `Produit : ${offerName}` : '',
      mutualiste
        ? `Titulaire : ${mutualiste.nom || ''} ${mutualiste.prenoms || ''}`
        : ''
    ].filter(Boolean);

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    policyInfoLines.forEach((line) => {
      doc.text(line, 15, y);
      y += 5;
    });

    // Résumé des montants
    y += 3;
    doc.setFontSize(11);
    doc.setTextColor(25, 25, 25);
    doc.text('Synthèse des cotisations', 15, y);
    y += 4;

    const totalExpected = (policy.contributions?.length || policy.expectedContributions || 0) * (policy.monthlyAmount || 0);
    const summaryRows = [
      ['Montant mensuel', `${formatAmount(policy.monthlyAmount)} FCFA`],
      ['Cotisations reçues', `${policy.receivedContributions || 0} / ${(policy.contributions?.length || policy.expectedContributions || 0)}`],
      ['Total reçu', `${formatAmount(policy.totalAmountReceived)} FCFA`],
      ['Total attendu', `${formatAmount(totalExpected)} FCFA`],
    ];

    autoTable(doc, {
      startY: y,
      head: [['Indicateur', 'Valeur']],
      body: summaryRows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175], halign: 'center', fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      columnStyles: {
        1: { halign: 'right' }
      },
    });

    // Historique des cotisations
    const contributions = Array.isArray(policy.contributions) ? policy.contributions.slice() : [];
    contributions.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

    if (contributions.length > 0) {
      let startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : y + 20;
      doc.setFontSize(11);
      doc.setTextColor(25, 25, 25);
      doc.text('Historique des cotisations', 15, startY);
      startY += 4;

      const body = contributions.map((c) => [
        c.dueDate ? new Date(c.dueDate).toLocaleDateString('fr-FR') : '',
        `${formatAmount(c.amount)} FCFA`,
        c.status === 'PAID' ? 'Payée' : c.status === 'OVERDUE' ? 'En retard' : 'En attente',
        c.paidDate ? new Date(c.paidDate).toLocaleDateString('fr-FR') : '-',
        c.paymentMethod || '-',
      ]);

      autoTable(doc, {
        startY,
        head: [['Échéance', 'Montant', 'Statut', 'Date paiement', 'Mode de paiement']],
        body,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 118, 110], halign: 'center', fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [244, 244, 245] },
        columnStyles: {
          1: { halign: 'right' }
        }
      });
    }

    // Pied de page
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.text("Document généré depuis l'espace FOPAM+", 15, pageHeight - 10);

    const fileName = `Releve_Cotisations_${policy.policyNumber || ''}.pdf`;
    doc.save(fileName);

    toast.success('Relevé de cotisations téléchargé');
  } catch (error) {
    console.error('Erreur génération PDF relevé cotisations santé:', error);
    toast.error('Erreur lors de la génération du relevé de cotisations');
  }
};

const HealthPolicyDetailsModal = ({
  policy,
  onClose,
  onRequestTermination,
  onOpenHealthReimbursement,
  onDownloadHealthStatement,
  showHealthActions = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedContributionYears, setExpandedContributionYears] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('MOBILE_MONEY');
  const [loadingPayId, setLoadingPayId] = useState(null);
  const [loadingDuplicataId, setLoadingDuplicataId] = useState(null);

  if (!policy) return null;

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const dob = new Date(birthDate);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const handlePayContribution = async (contribution) => {
    setLoadingPayId(contribution.id);
    await new Promise(r => setTimeout(r, 600));
    setLoadingPayId(null);
    setSelectedContribution(contribution);
    setPaymentMethod('MOBILE_MONEY');
    setShowPaymentModal(true);
  };

  const confirmPayment = () => {
    toast.success('Paiement en ligne - fonctionnalité à venir');
    setShowPaymentModal(false);
    setSelectedContribution(null);
  };

  const handleDownloadDuplicata = async (contribution) => {
    const paymentContrib = contribution.paymentContributions?.find(
      pc => pc.payment?.status === 'COMPLETED' && pc.payment?.receiptNumber
    );
    if (!paymentContrib?.payment) {
      toast.error('Aucun reçu disponible pour cette cotisation');
      return;
    }
    setLoadingDuplicataId(contribution.id);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_URL}/payments/${paymentContrib.payment.id}/receipt-duplicate`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `duplicata-${paymentContrib.payment.receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Duplicata téléchargé avec succès');
    } catch (error) {
      console.error('Erreur génération duplicata:', error);
      toast.error('Erreur lors de la génération du duplicata');
    } finally {
      setLoadingDuplicataId(null);
    }
  };

  const contributions = policy.contributions || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* Header aligné sur SavingsDetailsModal / AdminPolicies */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl leading-6 font-bold text-gray-900">
                  Détails de la police
                </h3>
                <p className="text-sm text-gray-500 mt-1 flex items-center flex-wrap gap-2">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span>
                      Police {policy.policyNumber}
                    </span>
                    {policy.status && (
                      <span className={`badge badge-${getStatusColor(policy.status?.libelle || policy.status)}`}>
                        {getStatusLabel(policy.status?.libelle || policy.status)}
                      </span>
                    )}
                    {policy.subscription?.offer?.name ? (
                      <span>
                        • {policy.subscription.offer.name}
                      </span>
                    ) : null}
                  </span>
                </p>
                {policy.subscription?.mutualiste && (
                  <p className="text-sm text-gray-500 mt-1">
                    {policy.subscription.mutualiste.nom} {policy.subscription.mutualiste.prenoms}
                    {policy.subscription.mutualiste.matricule
                      ? ` • ${policy.subscription.mutualiste.matricule}`
                      : ''}
                    {policy.subscription.mutualiste.genre?.libelle
                      ? ` • ${policy.subscription.mutualiste.genre.libelle}`
                      : ''}
                    {(() => {
                      const age = calculateAge(policy.subscription.mutualiste.dateNaissance);
                      return age !== null ? ` • ${age} ans` : '';
                    })()}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Synthèse / statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">Montant mensuel</p>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(policy.monthlyAmount)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs text-green-600 font-medium mb-1">Cotisations</p>
                <p className="text-sm text-gray-700 mb-1">
                  Nombre :{' '}
                  <span className="font-bold text-green-900">{policy.receivedContributions}</span>
                  <span className="text-gray-500 font-normal"> / {contributions.length || policy.expectedContributions}</span>
                </p>
                <p className="text-sm text-gray-700">
                  Montant :{' '}
                  <span className="font-bold text-green-900">{formatCurrency(policy.totalAmountReceived)}</span>
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="text-xs text-purple-600 font-medium mb-1">Dates essentielles</p>
                <p className="text-sm text-gray-700 mb-1">
                  Souscription :{' '}
                  <span className="font-bold text-gray-900">
                    {policy.subscription?.createdAt
                      ? new Date(policy.subscription.createdAt).toLocaleDateString('fr-FR')
                      : '-'}
                  </span>
                </p>
                <p className="text-sm text-gray-700">
                  Date d'effet :{' '}
                  <span className="font-bold text-gray-900">
                    {policy.effectiveDate
                      ? new Date(policy.effectiveDate).toLocaleDateString('fr-FR')
                      : '-'}
                  </span>
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <p className="text-xs text-teal-600 font-medium mb-1">Remboursements</p>
                <p className="text-lg font-bold text-teal-900">{formatCurrency(policy.totalHealthReimbursed || 0)}</p>
              </div>
            </div>

            {/* Cotisations */}
            {contributions.length > 0 && (
              <div className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border border-green-100">
                <div className="flex items-start mb-4">
                  <div className="flex items-center space-x-2">
                    <CreditCardIcon className="h-5 w-5 text-green-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Historique des opérations</h4>
                  </div>
                </div>

                {(() => {
                  const sortedContributions = [...contributions].sort((a, b) =>
                    new Date(b.dueDate) - new Date(a.dueDate)
                  );
                  const contributionsByYear = sortedContributions.reduce((acc, contrib) => {
                    const year = new Date(contrib.dueDate).getFullYear();
                    if (!acc[year]) acc[year] = [];
                    acc[year].push(contrib);
                    return acc;
                  }, {});
                  const years = Object.keys(contributionsByYear).sort((a, b) => b - a);

                  return (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {years.map((year) => {
                        const yearContributions = contributionsByYear[year];
                        const isExpanded = expandedContributionYears[year];

                        return (
                          <div key={year} className="border border-gray-200 rounded-lg">
                            <button
                              type="button"
                              onClick={() => setExpandedContributionYears(prev => ({
                                ...prev,
                                [year]: !prev[year]
                              }))}
                              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <span className="text-lg font-bold text-gray-900">{year}</span>
                              </div>
                              {isExpanded ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="border-t border-gray-200 p-4">
                                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                      <CreditCardIcon className="h-5 w-5 text-green-600" />
                                      <p className="font-semibold text-gray-900">Cotisations</p>
                                    </div>
                                    <p className="text-lg font-bold text-green-700">
                                      +{formatCurrency(yearContributions.filter(c => c.status === 'PAID').reduce((sum, c) => sum + parseFloat(c.amount || 0), 0))}
                                    </p>
                                  </div>
                                  <div className="overflow-x-auto rounded-lg">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Échéance</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Montant</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Statut</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Date paiement</th>
                                        <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {yearContributions.map((contribution, idx) => {
                                        const completedPayment = contribution.paymentContributions?.find(
                                          pc => pc.payment?.status === 'COMPLETED' && pc.payment?.receiptNumber
                                        );
                                        return (
                                        <tr key={contribution.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {new Date(contribution.dueDate).toLocaleDateString('fr-FR')}
                                          </td>
                                          <td className="px-4 py-3 text-sm font-bold text-green-600">
                                            {formatCurrency(contribution.amount)}
                                          </td>
                                          <td className="px-4 py-3 text-sm">
                                            <span className={`badge badge-${contribution.status === 'PAID' ? 'success' : contribution.status === 'OVERDUE' ? 'danger' : 'warning'}`}>
                                              {contribution.status === 'PAID' ? 'Payée' : contribution.status === 'OVERDUE' ? 'En retard' : 'En attente'}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            {contribution.paidDate ? new Date(contribution.paidDate).toLocaleDateString('fr-FR') : '-'}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-center">
                                            <div className="flex items-center justify-center space-x-2">
                                              {showHealthActions && contribution.status !== 'PAID' && (
                                                <button
                                                  type="button"
                                                  onClick={() => handlePayContribution(contribution)}
                                                  disabled={loadingPayId === contribution.id}
                                                  className="text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                  title="Payer cette cotisation"
                                                >
                                                  {loadingPayId === contribution.id ? (
                                                    <img src="/assets/logo-fopam-v2.png" alt="" className="h-5 w-5 animate-spin" />
                                                  ) : (
                                                    <CreditCardIcon className="h-5 w-5" />
                                                  )}
                                                </button>
                                              )}
                                              {completedPayment && (
                                                <button
                                                  type="button"
                                                  onClick={() => handleDownloadDuplicata(contribution)}
                                                  disabled={loadingDuplicataId === contribution.id}
                                                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                  title="Télécharger le duplicata de reçu"
                                                >
                                                  {loadingDuplicataId === contribution.id ? (
                                                    <img src="/assets/logo-fopam-v2.png" alt="" className="h-5 w-5 animate-spin" />
                                                  ) : (
                                                    <PrinterIcon className="h-5 w-5" />
                                                  )}
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Prestations de santé */}
            {policy.subscription?.offer?.type === 'HEALTH_COVERAGE' && Array.isArray(policy.healthServices) && policy.healthServices.length > 0 && (
              <div className="mt-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-5 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Prestations de santé</h4>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-lg shadow">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Bénéficiaire</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Établissement</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Montant total</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Ticket modérateur</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Surcoût</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {policy.healthServices.map((service) => (
                        <tr key={service.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {service.executedAt ? new Date(service.executedAt).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {service.type?.libelle || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {service.mutualiste ? `${service.mutualiste.nom} ${service.mutualiste.prenoms}` : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {service.etablissement || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {formatCurrency(service.costTotal || 0)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {formatCurrency(service.ticketModerateur || 0)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {formatCurrency(service.surcout || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Modal de paiement */}
            {showPaymentModal && selectedContribution && (
              <div className="fixed inset-0 z-[60] overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPaymentModal(false)} />
                  <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                          <CreditCardIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">Payer une cotisation</h3>
                          <div className="mt-2">
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-500">Police</p>
                                  <p className="font-medium">{policy.policyNumber}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Échéance</p>
                                  <p className="font-medium">{new Date(selectedContribution.dueDate).toLocaleDateString('fr-FR')}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Montant</p>
                                  <p className="font-medium text-lg text-green-600">{formatCurrency(selectedContribution.amount)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Statut</p>
                                  <span className={`badge badge-${selectedContribution.status === 'OVERDUE' ? 'danger' : 'warning'}`}>
                                    {selectedContribution.status === 'OVERDUE' ? 'En retard' : 'En attente'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4">
                              <label className="form-label">Méthode de paiement</label>
                              <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="form-input"
                              >
                                <option value="MOBILE_MONEY">Mobile Money</option>
                                <option value="BANK_TRANSFER">Virement bancaire</option>
                                <option value="CASH">Espèces</option>
                                <option value="CHECK">Chèque</option>
                              </select>
                            </div>
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                              <p className="text-sm text-blue-800">
                                <strong>Note :</strong> Le paiement en ligne sera bientôt disponible.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        onClick={confirmPayment}
                        disabled
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-400 text-base font-medium text-white cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Payer
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPaymentModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions spécifiques (mêmes boutons pour admin et mutualiste) */}
            {showHealthActions && (
              <div className="mt-6 px-6 pb-6">
                <div className="flex flex-col md:flex-row gap-3 items-stretch justify-start">
                  {onRequestTermination && (
                    <button
                      type="button"
                      onClick={() => onRequestTermination(policy)}
                      className="flex-1 btn flex items-center justify-center bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Demander la résiliation
                    </button>
                  )}
                  {onOpenHealthReimbursement && (
                    <button
                      type="button"
                      onClick={() => onOpenHealthReimbursement(policy)}
                      className="flex-1 btn-primary flex items-center justify-center"
                    >
                      <BanknotesIcon className="h-5 w-5 mr-2" />
                      Demander un remboursement
                    </button>
                  )}
                  {onDownloadHealthStatement && (
                    <button
                      type="button"
                      onClick={() => onDownloadHealthStatement(policy)}
                      className="flex-1 btn-success flex items-center justify-center"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      Télécharger un relevé
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthPolicyDetailsModal;
